/**
 * Facility Login Selector Component for TECDocChart
 * 
 * Simple facility selection modal that appears on login to prompt
 * the user to select which facility they are currently visiting.
 * Matches the AngularJS implementation behavior.
 * 
 * Issue #74 - Master Layout Migration
 */

import { useState, useEffect } from 'react';
import { tecDocToast } from '../../utils/toast';
import { useAPIService } from '../../services/apiService';
import { readFacilityScope } from '../../utils/facilityScope';
import { canViewQualityDashboard } from '../../utils/permissions';
import './FacilityLoginSelector.css';

/**
 * Sentinel for the "all facilities" choice (issue #243).
 *
 * Offered only to quality-dashboard users with more than one assigned location: it is the
 * Quality Dashboard that can span facilities, while every other screen needs one concrete
 * facility. The backend keeps FACILITY_ID pointed at an assigned facility either way.
 */
const ALL_FACILITIES = 'all' as const;

type FacilitySelection = number | typeof ALL_FACILITIES | null;

interface Facility {
  id: number;
  name: string;
  code: string | null;
  assigned: string; // "Assigned" or "Unassigned"
}

interface FacilityLoginSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onFacilitySelected: (facility: Facility) => void;
  globalContext: any;
}

/**
 * Read the facility array out of an /api/facilities/ response body.
 *
 * The endpoint wraps its payload -- {data: Facility[], count, timestamp} -- and
 * apiService returns the parsed body as `response.data`, so the array sits one level
 * deeper than the call site suggests. Reading it as `response.data` put a plain object
 * into facility state; nothing noticed until #243 added a render-time
 * `facilities.filter(...)`, which then threw on every render and took the shell down
 * through the ErrorBoundary.
 *
 * Returns null rather than throwing so the caller can fall through to the
 * /facility-setter path, and so a bad shape never reaches state.
 */
export function extractFacilityList(body: unknown): Facility[] | null {
  if (Array.isArray(body)) {
    return body as Facility[];
  }
  const wrapped = (body as { data?: unknown } | null | undefined)?.data;
  return Array.isArray(wrapped) ? (wrapped as Facility[]) : null;
}

function FacilityLoginSelector({ 
  isOpen, 
  onClose, 
  onFacilitySelected,
  globalContext 
}: FacilityLoginSelectorProps) {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState<FacilitySelection>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const apiService = useAPIService();

  useEffect(() => {
    if (isOpen) {
      loadFacilities();
      
      // Check for current facility and pre-select it
      const global = (window as any);
      const currentFacilityId = globalContext.tenantContext?.facilityId || 
                                global.facility?.id ||
                                global.loggedInEmployee?.current_facility_id;
      
      // A user already spanning every facility comes back to the modal on that choice.
      if (readFacilityScope().mode === 'all') {
        setSelectedFacilityId(ALL_FACILITIES);
      } else if (currentFacilityId) {
        setSelectedFacilityId(parseInt(currentFacilityId));
      }
    }
  }, [isOpen, globalContext]);

  const loadFacilities = async () => {
    try {
      setIsLoading(true);
      
      // Try JSON API endpoint first using API service
      try {
        const response = await apiService.get('/api/facilities/', 'Loading facilities...');

        const loaded = extractFacilityList(response.data);
        if (!loaded) {
          throw new Error('Unexpected /api/facilities/ response shape');
        }
        setFacilities(loaded);

        // Auto-select first assigned facility if only one available
        const assignedFacilities = loaded.filter((f: Facility) => f.assigned === "Assigned");
        if (assignedFacilities.length === 1) {
          setSelectedFacilityId(assignedFacilities[0].id);
        }
        return; // Success, exit early
        
      } catch (jsonError) {
        // JSON API not available, fall back to JavaScript endpoint
      }
      
      // Fallback: Get facilities from Django JavaScript endpoint manually
      // Note: Can't use API service here because this returns JavaScript, not JSON
      const response = await fetch('/facility-setter', {
        credentials: 'include',
        headers: {
          'Accept': 'application/javascript',
          'X-CSRFToken': globalContext?.authContext?.csrf_token || ''
        }
      });

      // Handle session expiration - redirect to login
      if (response.status === 401 || response.status === 403) {
        const currentPath = window.location.pathname + window.location.search;
        window.location.href = `/login?next=${encodeURIComponent(currentPath)}`;
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to load facilities: ${response.status} ${response.statusText}`);
      }

      const jsContent = await response.text();
      
      // Extract facilities data from the JavaScript response
      // The format uses facilities.push() calls, so we need to parse differently
      const facilities: Facility[] = [];
      
      // Find all facilities.push() calls
      const pushMatches = jsContent.matchAll(/facilities\.push\(\s*(\{[^}]*\})\s*\);/g);
      
      for (const match of pushMatches) {
        try {
          // Clean up the JavaScript object to be valid JSON
          let facilityStr = match[1];
          // Replace unquoted keys with quoted keys
          facilityStr = facilityStr.replace(/(\w+):/g, '"$1":');
          // Replace single quotes with double quotes
          facilityStr = facilityStr.replace(/'/g, '"');
          
          const facility = JSON.parse(facilityStr);
          facilities.push(facility);
        } catch (parseError) {
          console.warn('Failed to parse facility:', match[1], parseError);
        }
      }
      
      setFacilities(facilities);
        
      // Auto-select first assigned facility if only one available
      const assignedFacilities = facilities.filter((f: Facility) => f.assigned === "Assigned");
      if (assignedFacilities.length === 1) {
        setSelectedFacilityId(assignedFacilities[0].id);
      }
      
    } catch (error) {
      console.error('Error loading facilities:', error);
      tecDocToast.error('Failed to load facilities');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = async () => {
    if (selectedFacilityId === null) {
      tecDocToast.error('Please select a facility');
      return;
    }

    const spansAllFacilities = selectedFacilityId === ALL_FACILITIES;
    const selectedFacility = spansAllFacilities
      ? null
      : facilities.find(f => f.id === selectedFacilityId);

    if (!spansAllFacilities && !selectedFacility) {
      tecDocToast.error('Invalid facility selection');
      return;
    }

    try {
      setIsSelecting(true);

      // `scope` drives the wider choice; `id` stays the single-facility contract that the
      // legacy callers of /facility still use.
      await apiService.post('/facility',
        spansAllFacilities ? { scope: ALL_FACILITIES } : { id: selectedFacilityId },
        spansAllFacilities ? 'Selecting facilities...' : 'Selecting facility...'
      );

      // Notify parent component
      if (selectedFacility) {
        onFacilitySelected(selectedFacility);
      }

      // Close modal
      onClose();
      
      // Reload the page to refresh facility context
      setTimeout(() => {
        window.location.reload();
      }, 500);
      
    } catch (error) {
      console.error('Error setting facility:', error);
      tecDocToast.error('Failed to select facility');
    } finally {
      setIsSelecting(false);
    }
  };

  const getFacilityDisplayName = (facility: Facility) => {
    if (facility.code) {
      return `${facility.name} (${facility.code})`;
    }
    return facility.name;
  };

  // "All facilities" only makes sense for a quality user with somewhere to span: a single
  // assigned location is already the default, and non-quality screens have no "all" concept.
  const assignedFacilityCount = facilities.filter(f => f.assigned === 'Assigned').length;
  const canSpanAllFacilities = canViewQualityDashboard(globalContext) && assignedFacilityCount > 1;

  if (!isOpen) return null;

  return (
    <div className="facility-login-overlay">
      <div className="facility-login-modal" onClick={(e) => e.stopPropagation()}>
        <div className="facility-login-content">
          <h3 className="facility-login-title">
            Please choose the facility you are currently visiting:
          </h3>
          
          {isLoading ? (
            <div className="facility-login-loading">Loading facilities...</div>
          ) : (
            <>
              <select
                className="facility-login-select"
                value={selectedFacilityId ?? ''}
                onChange={(e) => {
                  const { value } = e.target;
                  if (value === ALL_FACILITIES) {
                    setSelectedFacilityId(ALL_FACILITIES);
                    return;
                  }
                  setSelectedFacilityId(parseInt(value) || null);
                }}
                disabled={isSelecting}
              >
                <option value="">Select a facility...</option>
                {canSpanAllFacilities && (
                  <option value={ALL_FACILITIES}>All facilities</option>
                )}
                {facilities.map((facility) => (
                  <option key={facility.id} value={facility.id}>
                    {getFacilityDisplayName(facility)}
                  </option>
                ))}
              </select>
              
              <button
                className="facility-login-button"
                onClick={handleSelect}
                disabled={selectedFacilityId === null || isSelecting}
              >
                {isSelecting ? 'Selecting...' : 'Select'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default FacilityLoginSelector;